// src/screens/academique/QcmModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  Stack,
  IconButton,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Button
} from "@material-ui/core";
import {
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Send as SendIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Star as StarIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import CoursService from '../../services/CoursService';

const QcmModal = ({ open, course, onClose, onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  
  // Timer pour chaque question
  useEffect(() => {
    let timer = null;
    
    if (open && timeRemaining !== null && timeRemaining > 0 && !submitting) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            // Passer à la question suivante automatiquement
            if (currentIndex < questions.length - 1) {
              handleNext();
              return questions[currentIndex + 1]?.timer_seconds || 30;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [open, timeRemaining, currentIndex, questions, submitting]);
  
  // Mettre à jour le timer quand la question change
  useEffect(() => {
    if (questions[currentIndex] && open) {
      const timerValue = parseInt(questions[currentIndex].timer_seconds) || 0;
      setTimeRemaining(timerValue > 0 ? timerValue : null);
    }
  }, [currentIndex, questions, open]);
  
  // Charger les questions quand le modal s'ouvre
  useEffect(() => {
    if (open && course) {
      loadQuestions();
      resetQuiz();
    }
  }, [open, course]);
  
  const loadQuestions = async () => {
    setLoading(true);
    try {
      const result = await CoursService.getQuestionsByCourse(course.id);
      
      if (result && result.status === true) {
        // Parser les questions si nécessaire
        const parsedQuestions = (result.data || []).map(q => ({
          ...q,
          // S'assurer que les options sont un tableau
          options: parseJsonField(q.options),
          correct_ans: parseJsonField(q.correct_ans),
          points: parseInt(q.points) || 1,
          timer_seconds: parseInt(q.timer_seconds) || 0
        }));
        
        setQuestions(parsedQuestions);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error('Erreur chargement questions:', error);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour parser les champs JSON
  const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };
  
  const resetQuiz = () => {
    setAnswers({});
    setCurrentIndex(0);
    setSubmitting(false);
  };
  
  const handleOptionSelect = (questionId, optionIndex, isMultiple) => {
    const currentAnswer = answers[questionId] || (isMultiple ? [] : null);
    
    if (isMultiple) {
      const newAnswer = Array.isArray(currentAnswer) ? [...currentAnswer] : [];
      const idx = newAnswer.indexOf(optionIndex);
      if (idx >= 0) {
        newAnswer.splice(idx, 1);
      } else {
        newAnswer.push(optionIndex);
      }
      setAnswers(prev => ({ ...prev, [questionId]: newAnswer }));
    } else {
      setAnswers(prev => ({ ...prev, [questionId]: [optionIndex] }));
    }
  };
  
  const handleOpenAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };
  
  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };
  
  const handleSubmit = async () => {
    if (!isAllQuestionsAnswered()) {
      return;
    }
    
    setSubmitting(true);
    
    // Calculer le score
    let totalPoints = 0;
    let earnedPoints = 0;
    const detailedResults = [];
    
    questions.forEach(q => {
      const points = q.points || 1;
      totalPoints += points;
      const userAnswer = answers[q.id];
      const isCorrect = checkAnswer(q, userAnswer);
      
      if (isCorrect) {
        earnedPoints += points;
      }
      
      detailedResults.push({
        question_id: q.id,
        question: q.question,
        user_answer: userAnswer,
        is_correct: isCorrect,
        points_earned: isCorrect ? points : 0,
        max_points: points
      });
    });
    
    const score = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;
    
    // Préparer les données à envoyer
    const submissionData = {
      course_id: course.id,
      score: score,
      earned_points: earnedPoints,
      total_points: totalPoints,
      answers: answers,
      detailed_results: detailedResults,
      completed_at: new Date().toISOString()
    };
    
    try {
      // Appeler le service pour enregistrer le résultat (à adapter selon votre API)
      // const result = await CoursService.submitQuizResult(course.id, submissionData);
      
      // Simulation d'attente
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const result = {
        course_id: course.id,
        courseTitle: course.title,
        score: score,
        earned_points: earnedPoints,
        total_points: totalPoints,
        date: new Date(),
        details: detailedResults
      };
      
      onComplete?.(result);
      onClose();
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  const checkAnswer = (question, userAnswer) => {
    if (!userAnswer) return false;
    
    // Question ouverte
    if (question.type === 'open') {
      return userAnswer.trim().length > 10; // Validation simple, à adapter selon les besoins
    }
    
    // Question à choix
    const correctAnswers = question.correct_ans || [];
    if (!correctAnswers.length) return false;
    
    if (question.type === 'unique') {
      return userAnswer[0] === correctAnswers[0];
    } else {
      // Choix multiple
      const sortedUser = [...userAnswer].sort((a, b) => a - b);
      const sortedCorrect = [...correctAnswers].sort((a, b) => a - b);
      return JSON.stringify(sortedUser) === JSON.stringify(sortedCorrect);
    }
  };
  
  const isQuestionAnswered = (question) => {
    const answer = answers[question.id];
    if (!answer) return false;
    if (question.type === 'open') {
      return answer.trim().length > 0;
    }
    if (Array.isArray(answer)) {
      return answer.length > 0;
    }
    return !!answer;
  };
  
  const isAllQuestionsAnswered = () => {
    return questions.every(q => isQuestionAnswered(q));
  };
  
  const getProgress = () => {
    if (questions.length === 0) return 0;
    const answered = questions.filter(q => isQuestionAnswered(q)).length;
    return (answered / questions.length) * 100;
  };
  
  const formatTime = (seconds) => {
    if (!seconds || seconds <= 0) return 'Illimité';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };
  
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const progress = getProgress();
  const isTimerWarning = timeRemaining !== null && timeRemaining <= 10;
  
  return (
    <Dialog
      open={open}
      onClose={!submitting ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            p: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#F8F9FA',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="700" display="flex" alignItems="center" gap={1}>
              <QuestionAnswerIcon sx={{ color: '#1976D2' }} />
              {course?.title || 'Questionnaire QCM'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {questions.length} question{questions.length > 1 ? 's' : ''}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={2}>
            {timeRemaining !== null && timeRemaining > 0 && (
              <Chip
                icon={<AccessTimeIcon />}
                label={formatTime(timeRemaining)}
                size="medium"
                sx={{
                  bgcolor: isTimerWarning ? 'error.light' : 'warning.light',
                  color: isTimerWarning ? 'error.dark' : 'warning.dark',
                  fontWeight: 600,
                  '& .MuiChip-icon': {
                    color: isTimerWarning ? 'error.dark' : 'warning.dark'
                  }
                }}
              />
            )}
            <IconButton onClick={onClose} size="small" disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            bgcolor: '#E9ECEF',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#1976D2'
            }
          }}
        />
      </DialogTitle>
      
      {/* Content */}
      <DialogContent sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Chargement des questions...
            </Typography>
          </Box>
        ) : questions.length === 0 ? (
          <Box textAlign="center" py={4}>
            <QuestionAnswerIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Aucune question disponible pour ce cours
            </Typography>
          </Box>
        ) : currentQuestion && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: '#F8F9FA',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            {/* Question header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="overline" fontWeight="700" color="primary">
                Question {currentIndex + 1} / {questions.length}
              </Typography>
              <Chip
                icon={<StarIcon sx={{ fontSize: 14 }} />}
                label={`${currentQuestion.points} pts`}
                size="small"
                variant="outlined"
              />
            </Box>
            
            {/* Question text */}
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 3, lineHeight: 1.5 }}>
              {currentQuestion.question}
            </Typography>
            
            {/* Options */}
            {currentQuestion.type === 'open' ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Votre réponse..."
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleOpenAnswerChange(currentQuestion.id, e.target.value)}
                disabled={submitting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#FFFFFF',
                    '&:hover fieldset': {
                      borderColor: '#1976D2'
                    }
                  }
                }}
              />
            ) : currentQuestion.type === 'unique' ? (
              <RadioGroup
                value={answers[currentQuestion.id]?.[0] ?? ''}
                onChange={(e) => handleOptionSelect(currentQuestion.id, parseInt(e.target.value), false)}
              >
                <Stack spacing={1.5}>
                  {currentQuestion.options && currentQuestion.options.map((opt, idx) => (
                    <FormControlLabel
                      key={idx}
                      value={idx}
                      control={<Radio disabled={submitting} />}
                      label={opt}
                      sx={{
                        m: 0,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: answers[currentQuestion.id]?.[0] === idx ? 'primary.main' : 'divider',
                        bgcolor: answers[currentQuestion.id]?.[0] === idx ? 'action.hover' : 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    />
                  ))}
                </Stack>
              </RadioGroup>
            ) : (
              // Multiple choice
              <Stack spacing={1.5}>
                {currentQuestion.options && currentQuestion.options.map((opt, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={answers[currentQuestion.id]?.includes(idx) || false}
                        onChange={() => handleOptionSelect(currentQuestion.id, idx, true)}
                        disabled={submitting}
                      />
                    }
                    label={opt}
                    sx={{
                      m: 0,
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: answers[currentQuestion.id]?.includes(idx) ? 'primary.main' : 'divider',
                      bgcolor: answers[currentQuestion.id]?.includes(idx) ? 'action.hover' : 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        )}
      </DialogContent>
      
      {/* Footer - Boutons agrandis */}
      <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handlePrevious}
          disabled={currentIndex === 0 || submitting || questions.length === 0}
          startIcon={<NavigateBeforeIcon />}
          sx={{ 
            borderRadius: 2, 
            px: 4, 
            py: 1.2,
            fontSize: '1rem',
            minWidth: 140,
            minHeight:40,
            fontWeight: 600
          }}
        >
          Précédent
        </Button>
        
        <Box sx={{ flex: 1 }} />
        
        {questions.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2, fontWeight: 500 }}>
            {Math.round(progress)}% complété
          </Typography>
        )}
        
        {questions.length > 0 && !isLastQuestion ? (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<NavigateNextIcon />}
            disabled={submitting}
            sx={{ 
              borderRadius: 2, 
              px: 4, 
              py: 1.2,
              fontSize: '1rem',
              minWidth: 140,
              minHeight:40,
              fontWeight: 600,
              bgcolor: '#1976D2',
              '&:hover': { bgcolor: '#1565C0' }
            }}
          >
            Suivant
          </Button>
        ) : questions.length > 0 && isLastQuestion ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !isAllQuestionsAnswered()}
            startIcon={!submitting && <SendIcon />}
            sx={{
              borderRadius: 2,
              px: 5,
              py: 1.2,
              fontSize: '1rem',
              minWidth: 180,
              minHeight:40,
              fontWeight: 700,
              bgcolor: '#28A745',
              '&:hover': { bgcolor: '#218838' },
              '&.Mui-disabled': {
                bgcolor: '#6C757D'
              }
            }}
          >
            {submitting ? 'Envoi en cours...' : 'Soumettre'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default QcmModal;